/**
 * mapNaptimeToProps
 *
 * Instead of having executeMutation inside your component, make your component
 * dumb by allowing it to just have handlers.
 *
 * Example:
 * const EnrollmentManager extends React.Component {
 *   static propTypes = {
 *     courseId: React.PropTypes.string.isRequired,
 *     handleEnroll: React.PropTypes.func,
 *     handleUnenroll: React.PropTypes.func
 *   }
 * }
 *
 * const mapFn = (naptime, props) => ({
 *   handleEnroll: () => {
 *     return naptime.executeMutation(CourseMembershipsV1.create(props.courseId));
 *   },
 *   handleUnenroll: () => {
 *     return naptime.executeMutation(CourseMembershipsV1.delete(props.courseId));
 *   },
 *   refreshEnrollments: () => {
 *   return naptime.refreshData({
 *     resources: 'courseMemberships.v1'
 *   })
 * })
 *
 * module.exports = _.compose(
 *   connectToStores(
 *     ['CourseStore'],
 *     ({CourseStore}) => ({
 *       courseId: CourseStore.getCourseId()
 *     })
 *   ),
 *   mapNaptimeToProps(mapFn)
 * )(EnrollButton);
 */
import * as React from 'react';

import PropTypes from 'prop-types';
import type Q from 'q';
import type { LegacyContextType } from 'types/legacy-context-types';

import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

interface NaptimeProps {
  refreshData({ elements, resources }: { elements?: string[]; resources?: string[] }): Q.Promise<void>;
  executeMutation(client: any, options?: any): Q.Promise<any>;
}

interface InferableComponentEnhancerWithProps<TInjectedProps, TNeedsProps> {
  <P extends TInjectedProps>(component: React.ComponentType<P>): React.ComponentClass<TInjectedProps & TNeedsProps>;
}

function mapNaptimeToProps<Props = any, MappedProps = any>(
  mapFn: (naptime: NaptimeProps, props: Props) => MappedProps
) {
  return function <P extends { naptime: any } & Props>(
    Component: React.ComponentClass<P, {}>
  ): React.ComponentClass<P & MappedProps, {}> {
    const componentName = Component.displayName || Component.name;

    class NaptimePropMapper extends React.Component<P & MappedProps, {}> {
      static displayName = componentName + 'NaptimePropMapper';

      static contextTypes = {
        executeMutation: PropTypes.func,
      };

      declare context: LegacyContextType<typeof NaptimePropMapper.contextTypes>;

      getMappedNaptimeProps() {
        if (!this.props.naptime) {
          throw new Error(`${NaptimePropMapper.displayName} could not find a naptime prop. Make sure this is spawned
            after a Naptime.createContainer`);
        }

        const naptime = {
          ...this.props.naptime,
          executeMutation: this.context.executeMutation,
        };

        return mapFn(naptime, this.props);
      }

      render() {
        return React.createElement(Component, Object.assign({}, this.props, this.getMappedNaptimeProps()));
      }
    }
    hoistNonReactStatics(NaptimePropMapper, Component);

    return NaptimePropMapper;
  };
}

export default mapNaptimeToProps;
